import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { leadsAPI, contactAPI, supabase } from '../../lib/supabase';
import DealManagement from './DealManagement';
import CustomerManager from './CustomerManager';
import SafeIcon from '../../common/SafeIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import '../../index.css';

import { FiUsers, FiMessageSquare, FiMail, FiTrendingUp, FiFileText, FiBarChart2, FiPieChart, FiDollarSign, FiUser, FiTruck, FiEdit, FiSettings, FiLogOut } from 'react-icons/fi';

const AdminDashboard = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    totalInquiries: 0,
    newInquiries: 0,
    totalLeads: 0,
    convertedLeads: 0,
    todayAppointments: 0,
    completedAppointments: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [chartData, setChartData] = useState({
    golfCarts: [],
    partSales: [],
    vehicleSales: [],
    leads: [],
    conversionRates: [],
    monthlyTrends: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load actual data from database
      const today = new Date().toISOString().split('T')[0];
      const [inquiries, leads, appointments] = await Promise.all([
        contactAPI.getInquiries(),
        leadsAPI.getLeads(),
        // Service appointments - skip if table doesn't exist
        supabase
          .from('service_appointments')
          .select('*')
          .eq('appointment_date', today)
          .then(result => result.data || [])
      ]);

      // Calculate stats
      const newInquiries = inquiries.filter(inquiry => inquiry.status === 'new').length;
      const convertedLeads = leads.filter(lead => lead.status === 'converted').length;
      const todayAppointments = appointments.data?.length || 0;
      const completedAppointments = appointments.data?.filter(apt => apt.status === 'completed').length || 0;

      setStats({
        totalInquiries: inquiries.length,
        newInquiries: newInquiries,
        totalLeads: leads.length,
        convertedLeads: convertedLeads,
        todayAppointments: todayAppointments,
        completedAppointments: completedAppointments
      });

      // Generate monthly chart data
      const monthlyData = generateMonthlyChartData(inquiries, leads);
      setChartData(monthlyData);

      // Create recent activity from actual data
      const recentInquiries = inquiries.slice(0, 3).map(inquiry => ({
        id: `inquiry-${inquiry.id}`,
        type: 'inquiry',
        message: `New inquiry from ${inquiry.name}`,
        time: getTimeAgo(inquiry.created_at)
      }));

      const recentLeads = leads.slice(0, 2).map(lead => ({
        id: `lead-${lead.id}`,
        type: 'lead',
        message: `New lead from ${lead.first_name} ${lead.last_name}`,
        time: getTimeAgo(lead.created_at)
      }));

      const recentAppointments = appointments.data?.slice(0, 2).map(apt => ({
        id: `apt-${apt.id}`,
        type: 'appointment',
        message: `Service appointment for ${apt.customer_name}`,
        time: `${apt.appointment_time}`
      })) || [];

      setRecentActivity([...recentInquiries, ...recentLeads, ...recentAppointments].slice(0, 5));
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setLoading(false);
    }
  };

  const generateMonthlyChartData = (inquiries, leads) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();
    
    // Initialize monthly data structure
    const monthlyStats = months.map(month => ({
      month,
      golfCartInquiries: 0,
      golfCartSales: 0,
      partInquiries: 0,
      partSales: 0,
      vehicleInquiries: 0,
      vehicleSales: 0,
      totalLeads: 0,
      convertedLeads: 0
    }));

    // Process inquiries by category and month
    inquiries.forEach(inquiry => {
      const date = new Date(inquiry.created_at);
      if (date.getFullYear() === currentYear) {
        const monthIndex = date.getMonth();
        const category = inquiry.inquiry_type || inquiry.subject?.toLowerCase() || '';
        
        if (category.includes('golf') || category.includes('cart')) {
          monthlyStats[monthIndex].golfCartInquiries++;
          if (inquiry.status === 'converted') monthlyStats[monthIndex].golfCartSales++;
        } else if (category.includes('part') || category.includes('service')) {
          monthlyStats[monthIndex].partInquiries++;
          if (inquiry.status === 'converted') monthlyStats[monthIndex].partSales++;
        } else {
          monthlyStats[monthIndex].vehicleInquiries++;
          if (inquiry.status === 'converted') monthlyStats[monthIndex].vehicleSales++;
        }
      }
    });

    // Process leads by month
    leads.forEach(lead => {
      const date = new Date(lead.created_at);
      if (date.getFullYear() === currentYear) {
        const monthIndex = date.getMonth();
        monthlyStats[monthIndex].totalLeads++;
        if (lead.status === 'converted') monthlyStats[monthIndex].convertedLeads++;
      }
    });

    return {
      golfCarts: monthlyStats.map(m => ({
        month: m.month,
        inquiries: m.golfCartInquiries,
        sales: m.golfCartSales,
        conversionRate: m.golfCartInquiries > 0 ? ((m.golfCartSales / m.golfCartInquiries) * 100).toFixed(1) : 0
      })),
      partSales: monthlyStats.map(m => ({
        month: m.month,
        inquiries: m.partInquiries,
        sales: m.partSales,
        conversionRate: m.partInquiries > 0 ? ((m.partSales / m.partInquiries) * 100).toFixed(1) : 0
      })),
      vehicleSales: monthlyStats.map(m => ({
        month: m.month,
        inquiries: m.vehicleInquiries,
        sales: m.vehicleSales,
        conversionRate: m.vehicleInquiries > 0 ? ((m.vehicleSales / m.vehicleInquiries) * 100).toFixed(1) : 0
      })),
      leads: monthlyStats.map(m => ({
        month: m.month,
        total: m.totalLeads,
        converted: m.convertedLeads,
        conversionRate: m.totalLeads > 0 ? ((m.convertedLeads / m.totalLeads) * 100).toFixed(1) : 0
      })),
      conversionRates: monthlyStats.map(m => ({
        month: m.month,
        golfCarts: m.golfCartInquiries > 0 ? ((m.golfCartSales / m.golfCartInquiries) * 100).toFixed(1) : 0,
        parts: m.partInquiries > 0 ? ((m.partSales / m.partInquiries) * 100).toFixed(1) : 0,
        vehicles: m.vehicleInquiries > 0 ? ((m.vehicleSales / m.vehicleInquiries) * 100).toFixed(1) : 0,
        leads: m.totalLeads > 0 ? ((m.convertedLeads / m.totalLeads) * 100).toFixed(1) : 0
      })),
      monthlyTrends: monthlyStats.map(m => ({
        month: m.month,
        totalInquiries: m.golfCartInquiries + m.partInquiries + m.vehicleInquiries,
        totalSales: m.golfCartSales + m.partSales + m.vehicleSales,
        totalLeads: m.totalLeads
      }))
    };
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Less than an hour ago';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  const statCards = [
    {
      title: 'Total Inquiries',
      value: stats.totalInquiries,
      icon: FiMessageSquare,
      description: 'Contact form submissions'
    },
    {
      title: 'New Inquiries',
      value: stats.newInquiries,
      icon: FiMail,
      description: 'Unread inquiries'
    },
    {
      title: 'Sales Leads',
      value: stats.totalLeads,
      icon: FiUsers,
      description: 'Qualified leads'
    },
    {
      title: 'Converted',
      value: stats.convertedLeads,
      icon: FiTrendingUp,
      description: 'Successful sales'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-black-900">Dashboard</h1>
        <p className="text-sm sm:text-base text-black-900 opacity-80">Welcome back! Here's what's happening at Paradise-Performance.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((card, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="n-royal cursor-pointer hover:scale-105 transition-transform"
            onClick={() => {
              if (card.title === 'Sales Leads' || card.title === 'Converted') {
                setActiveTab && setActiveTab('leads');
              } else if (card.title === 'New Inquiries' || card.title === 'Total Inquiries') {
                setActiveTab && setActiveTab('leads');
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-primary-900 opacity-75">{card.title}</p>
                <p className="text-2xl sm:text-3xl font-bold text-primary-900">{card.value}</p>
              </div>
              <div className="n-circle">
                <SafeIcon icon={card.icon} className="w-6 h-6 text-primary-100" />
              </div>
            </div>
            <p className="text-xs text-primary-900 opacity-50 mt-2">{card.description}</p>
          </motion.div>
        ))}
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="n-royal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-primary-900">Recent Activity</h2>
          <button 
            onClick={() => setShowAllActivity(!showAllActivity)}
            className="text-white n-red hover:text-blue-600 font-medium">
            {showAllActivity ? 'Show Less' : 'View All'}
          </button>
        </div>
        <div className="space-y-4">
          {(showAllActivity ? recentActivity : recentActivity.slice(0, 5)).map((activity) => (
            <div key={activity.id} className="flex items-start space-x-4 p-3 n-royal rounded-lg group">
              <div className="w-2 h-2 bg-gray-600 rounded-full mt-2"></div>
              <div className="flex-1">
                <p className="font-medium text-primary-900">{activity.message}</p>
                <p className="text-xs sm:text-sm text-primary-900 opacity-75 mt-1">{activity.time}</p>
              </div>
              <button
                onClick={() => {
                  setRecentActivity(recentActivity.filter(a => a.id !== activity.id));
                }}
                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
              >
                <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 mt-12 mb-12 py-8 px-4 bg-gray-200 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Golf Cart Sales */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiBarChart2} className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-blue-600" />
              <span className="hidden sm:inline">Golf Cart Sales</span>
              <span className="sm:hidden">Golf Carts</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData.golfCarts}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="inquiries" fill="#3B82F6" name="Inquiries" />
              <Bar dataKey="sales" fill="#10B981" name="Sales" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Lead Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiUsers} className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-purple-600" />
              <span className="hidden sm:inline">Lead Performance</span>
              <span className="sm:hidden">Leads</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.leads}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#8B5CF6" name="Total Leads" strokeWidth={2} />
              <Line type="monotone" dataKey="converted" stroke="#10B981" name="Converted" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Part Sales Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiFileText} className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-amber-600" />
              <span className="hidden sm:inline">Parts & Service</span>
              <span className="sm:hidden">Parts</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData.partSales}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="inquiries" fill="#F59E0B" name="Inquiries" />
              <Bar dataKey="sales" fill="#10B981" name="Sales" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Vehicle Sales Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-rose-600" />
              <span className="hidden sm:inline">Vehicle Sales</span>
              <span className="sm:hidden">Vehicles</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.vehicleSales}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="inquiries" stroke="#F43F5E" name="Inquiries" strokeWidth={2} />
              <Line type="monotone" dataKey="sales" stroke="#10B981" name="Sales" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Conversion Rates Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-emerald-600" />
              <span className="hidden sm:inline">Conversion Rates Trend</span>
              <span className="sm:hidden">Conversion</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.conversionRates}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [`${value}%`, 'Conversion Rate']} />
              <Legend />
              <Line type="monotone" dataKey="golfCarts" stroke="#10B981" name="Golf Carts" strokeWidth={2} />
              <Line type="monotone" dataKey="parts" stroke="#8B5CF6" name="Parts" strokeWidth={2} />
              <Line type="monotone" dataKey="vehicles" stroke="#3B82F6" name="Vehicles" strokeWidth={2} />
              <Line type="monotone" dataKey="leads" stroke="#F59E0B" name="Leads" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Monthly Trends Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="bg-green-200 p-4 sm:p-6 rounded-xl shadow-lg"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiPieChart} className="w-4 sm:w-5 h-4 sm:h-5 mr-2 text-red-600" />
              <span className="hidden sm:inline">Monthly Business Overview</span>
              <span className="sm:hidden">Overview</span>
            </h3>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData.monthlyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="totalInquiries" stroke="#3B82F6" name="Total Inquiries" strokeWidth={2} />
              <Line type="monotone" dataKey="totalSales" stroke="#10B981" name="Total Sales" strokeWidth={2} />
              <Line type="monotone" dataKey="totalLeads" stroke="#F59E0B" name="Total Leads" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Performance Tables */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="n-royal"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-primary-900">Performance Summary</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="n-royal">
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-primary-900">
                {((stats.convertedLeads / stats.totalLeads) * 100 || 0).toFixed(1)}%
              </div>
              <p className="text-xs sm:text-sm text-primary-900 opacity-75 mt-1">
                Lead Conversion Rate
              </p>
            </div>
          </div>
          <div className="n-royal">
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-primary-900">
                {stats.totalInquiries + stats.totalLeads}
              </div>
              <p className="text-xs sm:text-sm text-primary-900 opacity-75 mt-1">
                Total Interactions
              </p>
            </div>
          </div>
          <div className="n-royal">
            <div>
              <div className="text-2xl sm:text-3xl font-bold text-primary-900">
                {stats.newInquiries}
              </div>
              <p className="text-xs sm:text-sm text-primary-900 opacity-75 mt-1">
                Pending Responses
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3 }}
        className="bg-white p-4 sm:p-6 rounded-lg shadow-lg"
      >
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <button className="flex items-center space-x-3 p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors">
            <SafeIcon icon={FiMessageSquare} className="w-6 h-6 text-primary-600" />
            <span className="font-medium text-gray-900">View Inquiries</span>
          </button>
          <button 
            onClick={() => onNavigate('leads')}
            className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <SafeIcon icon={FiUsers} className="w-6 h-6 text-green-600" />
            <span className="font-medium text-gray-900">View All Leads</span>
          </button>
          <button className="flex items-center space-x-3 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <SafeIcon icon={FiFileText} className="w-6 h-6 text-blue-600" />
            <span className="font-medium text-gray-900">Update Content</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;
